import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPriceCascadesBatch, getPostsWithSlugs, getPriceCascades } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, ArrowDown, ArrowDownUp, FileCode, Globe, Loader2, Plus, X } from 'lucide-react';
import type {
    CascadeBatchAdUnit,
    CascadeGranulation,
    CascadeStepsConfig,
    CascadeUnitGranulation,
    PriceCascade,
} from '@/types/price-rules';
import type { Domain } from '@/types/domain';
import { GranulationSelect, PostPicker, SectionLabel, cascadeKey as keyOf, normalizeSlug } from './CascadeModalParts';

const AD_UNITS: CascadeBatchAdUnit[] = ['Rewarded', 'Topo', 'Interstitial'];

type UnitState = Record<CascadeBatchAdUnit, { checked: boolean; granulationId: number | null }>;

const allUnitsChecked = (): UnitState => ({
    Rewarded: { checked: true, granulationId: null },
    Topo: { checked: true, granulationId: null },
    Interstitial: { checked: true, granulationId: null },
});

export type CreateCascadePrefill = {
    domainId?: number | null;
    originPost?: string | null;
    /** Destino ja conhecido pelo ID (tem cascade proprio) */
    destinationPost?: string | null;
    /** Destino conhecido so pela slug do redirect_url — o modal acha o ID na lista de posts */
    destinationSlug?: string | null;
    /** Abre so para configurar o destino: nada marcado na origem nem Rewarded no destino */
    destinationOnly?: boolean;
};

const noUnitsChecked = (): UnitState => ({
    Rewarded: { checked: false, granulationId: null },
    Topo: { checked: false, granulationId: null },
    Interstitial: { checked: false, granulationId: null },
});

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    prefill: CreateCascadePrefill;
    domains: Domain[] | undefined;
    granulations: CascadeGranulation[] | undefined;
    cascadeStepsConfig: CascadeStepsConfig | undefined;
}

/**
 * Modal "Nova Cascata": varios posts de origem (P1) com os mesmos ad units e
 * granulacoes, apontando para um post de destino (P2). Tudo sai numa chamada
 * so ao ms-price-rules, que valida o lote inteiro antes de gravar.
 *
 * Pais, modo de blocos e timeout nao aparecem: nunca variam na pratica e o
 * backend grava os valores fixos.
 */
export default function CreateCascadeModal({ open, onOpenChange, prefill, domains, granulations, cascadeStepsConfig }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [domainId, setDomainId] = useState<number | null>(null);
    const [originPosts, setOriginPosts] = useState<string[]>(['']);
    const [originUnits, setOriginUnits] = useState<UnitState>(allUnitsChecked);
    const [destPost, setDestPost] = useState('');
    const [destUnits, setDestUnits] = useState<UnitState>(allUnitsChecked);
    const [pendingDestSlug, setPendingDestSlug] = useState<string | null>(null);

    // Estado limpo a cada abertura, a partir do atalho que abriu o modal
    useEffect(() => {
        if (!open) return;
        setDomainId(prefill.domainId ?? null);
        setOriginPosts([prefill.originPost || '']);
        setOriginUnits(prefill.destinationOnly ? noUnitsChecked() : allUnitsChecked());
        setDestPost(prefill.destinationPost || '');
        setDestUnits(prefill.destinationOnly
            ? { ...allUnitsChecked(), Rewarded: { checked: false, granulationId: null } }
            : allUnitsChecked());
        setPendingDestSlug(prefill.destinationPost ? null : normalizeSlug(prefill.destinationSlug) || null);
    }, [open, prefill]);

    const { data: postsData, isLoading: postsLoading } = useQuery({
        queryKey: ['posts-with-slugs', domainId],
        queryFn: () => getPostsWithSlugs(domainId!),
        enabled: open && !!domainId,
    });
    const posts = postsData?.posts || [];

    const { data: domainCascades } = useQuery({
        queryKey: ['price-cascades', domainId],
        queryFn: () => getPriceCascades(domainId!),
        enabled: open && !!domainId,
    });

    const existing = useMemo(() => {
        const map = new Map<string, PriceCascade>();
        for (const c of domainCascades || []) map.set(keyOf(c.id_post_wp, c.ad_unit), c);
        return map;
    }, [domainCascades]);

    const slugOf = useMemo(() => {
        const map = new Map<string, string>();
        for (const p of posts) if (p.slug) map.set(p.id_post_wp, p.slug);
        for (const c of domainCascades || []) if (c.id_post_wp && c.slug) map.set(c.id_post_wp, c.slug);
        return (id: string) => map.get(id) || null;
    }, [posts, domainCascades]);

    // Atalho "+" do destino: so temos a slug; acha o ID quando os posts carregarem
    useEffect(() => {
        if (!pendingDestSlug || destPost) return;
        const fromCascades = (domainCascades || []).find(c => c.id_post_wp && normalizeSlug(c.slug) === pendingDestSlug);
        const fromPosts = posts.find(p => normalizeSlug(p.slug) === pendingDestSlug);
        const id = fromCascades?.id_post_wp || fromPosts?.id_post_wp;
        if (id) {
            setDestPost(id);
            setPendingDestSlug(null);
        }
    }, [pendingDestSlug, destPost, posts, domainCascades]);

    const filledOrigins = Array.from(new Set(originPosts.map(p => p.trim()).filter(Boolean)));
    const dest = destPost.trim();

    const originConflicts = (unit: CascadeBatchAdUnit) =>
        (filledOrigins.length ? filledOrigins : [null])
            .map(p => existing.get(keyOf(p, unit)))
            .filter((c): c is PriceCascade => !!c);

    // No destino, Topo/Interstitial que ja existem ficam travados. O Rewarded do
    // destino nao e cascade do post (e a granulacao pos-redirect), nunca conflita.
    const destExisting = (unit: CascadeBatchAdUnit) =>
        dest && unit !== 'Rewarded' ? existing.get(keyOf(dest, unit)) || null : null;

    const effOrigin = (u: CascadeBatchAdUnit) => originUnits[u].checked;
    const effDest = (u: CascadeBatchAdUnit) => !!dest && destUnits[u].checked && !destExisting(u);

    const originCount = AD_UNITS.filter(effOrigin).length * Math.max(filledOrigins.length, 1);
    const destCount = AD_UNITS.filter(u => u !== 'Rewarded' && effDest(u)).length;

    const errors: string[] = [];
    const warnings: string[] = [];
    if (!domainId) errors.push('Selecione um domínio');
    if (domainId && originCount + destCount === 0) errors.push('Marque ao menos um ad unit');
    if (filledOrigins.length && !AD_UNITS.some(effOrigin)) errors.push('Marque ao menos um ad unit no Post #1');
    for (const u of AD_UNITS) {
        if (!effOrigin(u)) continue;
        const found = originConflicts(u);
        if (found.length) {
            const onde = found.map(c => (c.slug ? `/${c.slug}` : c.id_post_wp ? `post ${c.id_post_wp}` : 'global')).join(', ');
            errors.push(`${u} já existe em ${onde} — desmarque ou tire o post`);
        }
    }
    if (dest && filledOrigins.includes(dest)) errors.push('O post de destino não pode ser também um post de origem');
    if (dest && effDest('Rewarded') && !effOrigin('Rewarded')) {
        errors.push('Rewarded no Post #2 é a granulação pós-redirect — marque também o Rewarded no Post #1');
    }
    if (domainId && !filledOrigins.length && AD_UNITS.some(effOrigin)) {
        warnings.push('Sem post de origem, a regra vale para todos os posts do site');
    }
    if (effOrigin('Rewarded') && !dest) warnings.push('Sem post de destino, o Rewarded não redireciona');

    const createMutation = useMutation({
        mutationFn: createPriceCascadesBatch,
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['price-cascades'] });
            toast({ title: `${res.total} cascata${res.total !== 1 ? 's' : ''} criada${res.total !== 1 ? 's' : ''}` });
            onOpenChange(false);
        },
        onError: (err: any) => {
            toast({ title: 'Erro ao criar cascata', description: err?.response?.data?.detail || 'Erro desconhecido', variant: 'destructive' });
        },
    });

    const toUnits = (state: UnitState, eff: (u: CascadeBatchAdUnit) => boolean): CascadeUnitGranulation[] =>
        AD_UNITS.filter(eff).map(u => ({ ad_unit: u, granulation_id: state[u].granulationId }));

    const handleSubmit = () => {
        if (errors.length || !domainId) return;
        createMutation.mutate({
            domain_id: domainId,
            origin_posts: filledOrigins,
            origin_units: toUnits(originUnits, effOrigin),
            destination_post: dest || null,
            destination_units: dest ? toUnits(destUnits, effDest) : [],
        });
    };

    const domainOptions = (domains || []).map(d => ({ value: String(d.id), label: d.domain }));
    const takenIds = [...filledOrigins, dest].filter(Boolean);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-purple-100">
                            <ArrowDownUp className="h-4 w-4 text-purple-600" />
                        </div>
                        Nova Cascata
                    </DialogTitle>
                    <DialogDescription>Configure uma regra de waterfall bidding</DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    {/* Dominio */}
                    <div className="space-y-2">
                        <SectionLabel icon={<Globe className="h-3.5 w-3.5 text-blue-600" />} tone="bg-blue-100">Domínio</SectionLabel>
                        <SearchableSelect
                            value={domainId ? String(domainId) : undefined}
                            onValueChange={(v) => {
                                setDomainId(Number(v));
                                setOriginPosts(['']);
                                setDestPost('');
                                setPendingDestSlug(null);
                            }}
                            options={domainOptions}
                            placeholder="Selecione um domínio"
                            searchPlaceholder="Buscar domínio..."
                            className="h-10 rounded-lg"
                        />
                    </div>

                    {/* Post #1 — origem(ns) */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <SectionLabel icon={<FileCode className="h-3.5 w-3.5 text-indigo-600" />} tone="bg-indigo-100">
                                Post #1 <span className="font-normal text-gray-400">· origem</span>
                            </SectionLabel>
                            <button
                                type="button"
                                onClick={() => setOriginPosts(list => [...list, ''])}
                                disabled={!domainId}
                                className="inline-flex items-center justify-center h-6 w-6 rounded-full border border-indigo-200 text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Adicionar outro post de origem para o mesmo destino"
                                aria-label="Adicionar outro post de origem"
                            >
                                <Plus className="h-3.5 w-3.5" />
                            </button>
                            <span className="text-xs text-gray-400 ml-auto">opcional</span>
                        </div>

                        {originPosts.map((value, i) => (
                            <div key={i} className="space-y-1">
                                {originPosts.length > 1 && (
                                    <span className="text-[11px] font-medium text-indigo-600">Origem {i + 1}</span>
                                )}
                                <PostPicker
                                    value={value}
                                    onChange={(v) => setOriginPosts(list => list.map((p, j) => (j === i ? v : p)))}
                                    onRemove={originPosts.length > 1 ? () => setOriginPosts(list => list.filter((_, j) => j !== i)) : undefined}
                                    posts={posts}
                                    loading={postsLoading}
                                    hasDomain={!!domainId}
                                    slugOf={slugOf}
                                    takenIds={takenIds.filter(id => id !== value)}
                                />
                            </div>
                        ))}

                        <UnitRows
                            units={originUnits}
                            onChange={setOriginUnits}
                            granulations={granulations}
                            cascadeStepsConfig={cascadeStepsConfig}
                            renderNote={(u) => {
                                if (!effOrigin(u)) return null;
                                const found = originConflicts(u);
                                return found.length ? <span className="text-red-600">já existe{found.length > 1 ? ` em ${found.length} posts` : ''}</span> : null;
                            }}
                        />
                    </div>

                    <div className="flex items-center gap-2 text-xs text-gray-400">
                        <div className="h-px flex-1 bg-gray-200" />
                        <ArrowDown className="h-3.5 w-3.5" />
                        redireciona para
                        <div className="h-px flex-1 bg-gray-200" />
                    </div>

                    {/* Post #2 — destino */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <SectionLabel icon={<FileCode className="h-3.5 w-3.5 text-amber-600" />} tone="bg-amber-100">
                                Post #2 <span className="font-normal text-gray-400">· destino</span>
                            </SectionLabel>
                            <span className="text-xs text-gray-400 ml-auto">opcional</span>
                        </div>

                        {pendingDestSlug && !dest && (
                            <p className="text-xs text-amber-700">
                                Procurando /{pendingDestSlug} na lista de posts{postsLoading ? '…' : ' — não achei, digite o ID dele'}
                            </p>
                        )}

                        <PostPicker
                            value={destPost}
                            onChange={(v) => { setDestPost(v); setPendingDestSlug(null); }}
                            posts={posts}
                            loading={postsLoading}
                            hasDomain={!!domainId}
                            slugOf={slugOf}
                            takenIds={filledOrigins}
                        />

                        <div className={dest ? '' : 'opacity-50 pointer-events-none'}>
                            <UnitRows
                                units={destUnits}
                                onChange={setDestUnits}
                                granulations={granulations}
                                cascadeStepsConfig={cascadeStepsConfig}
                                isDestination
                                disabledUnit={(u) => !!destExisting(u)}
                                renderNote={(u) => {
                                    const found = destExisting(u);
                                    if (found) {
                                        return (
                                            <span className="text-gray-500">
                                                já existe · {found.granulation_name || 'Padrão'}{found.status !== 'active' ? ' (inativa)' : ''}
                                            </span>
                                        );
                                    }
                                    return u === 'Rewarded' ? <span className="text-gray-400">pós-redirect</span> : null;
                                }}
                            />
                            {!dest && <p className="text-xs text-gray-400 mt-1.5">Escolha o post de destino para configurar os ad units dele</p>}
                        </div>
                    </div>

                    {(errors.length > 0 || warnings.length > 0) && domainId && (
                        <div className="space-y-1">
                            {errors.map(e => (
                                <p key={e} className="flex items-start gap-1.5 text-xs text-red-600">
                                    <X className="h-3.5 w-3.5 shrink-0 mt-px" />{e}
                                </p>
                            ))}
                            {warnings.map(w => (
                                <p key={w} className="flex items-start gap-1.5 text-xs text-amber-700">
                                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />{w}
                                </p>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                        {domainId && errors.length === 0 && (
                            <span className="text-xs text-gray-500 mr-auto">
                                {originCount + destCount} cascata{originCount + destCount !== 1 ? 's' : ''}
                                {destCount > 0 && ` (${originCount} na origem, ${destCount} no destino)`}
                            </span>
                        )}
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                        <Button onClick={handleSubmit} disabled={errors.length > 0 || createMutation.isPending}>
                            {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            Criar Cascata
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function UnitRows({ units, onChange, granulations, cascadeStepsConfig, isDestination, disabledUnit, renderNote }: {
    units: UnitState;
    onChange: (next: UnitState) => void;
    granulations: CascadeGranulation[] | undefined;
    cascadeStepsConfig: CascadeStepsConfig | undefined;
    isDestination?: boolean;
    disabledUnit?: (u: CascadeBatchAdUnit) => boolean;
    renderNote?: (u: CascadeBatchAdUnit) => ReactNode;
}) {
    const set = (u: CascadeBatchAdUnit, patch: Partial<UnitState[CascadeBatchAdUnit]>) =>
        onChange({ ...units, [u]: { ...units[u], ...patch } });

    return (
        <div className="space-y-1.5">
            <div>
                <div className="text-sm font-semibold text-gray-800">Ad Units e Granulação</div>
                <div className="text-xs text-gray-500">Ative os ad units que deseja incluir e defina a granulação de cada um.</div>
            </div>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {AD_UNITS.map(u => {
                    const disabled = disabledUnit?.(u) ?? false;
                    const checked = units[u].checked && !disabled;
                    // No destino, "vazio" no Rewarded nao e o padrao: o script cai na granulacao da origem
                    const inheritsFromOrigin = isDestination && u === 'Rewarded';
                    const note = renderNote?.(u);
                    return (
                        <div key={u} className="flex items-center gap-3 px-3 py-2">
                            <Checkbox
                                id={`${isDestination ? 'dest' : 'orig'}-${u}`}
                                checked={checked}
                                disabled={disabled}
                                onCheckedChange={(v) => set(u, { checked: v === true })}
                                className="border-purple-400 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                            />
                            <label htmlFor={`${isDestination ? 'dest' : 'orig'}-${u}`} className="min-w-0 cursor-pointer">
                                <span className={`block text-sm font-medium ${checked ? 'text-gray-900' : 'text-gray-400'}`}>{u}</span>
                                {note && <span className="block text-[11px] leading-tight">{note}</span>}
                            </label>
                            <GranulationSelect
                                value={units[u].granulationId}
                                onChange={(v) => set(u, { granulationId: v })}
                                adUnit={u}
                                granulations={granulations}
                                cascadeStepsConfig={cascadeStepsConfig}
                                inheritLabel={inheritsFromOrigin ? 'Usar a da origem' : undefined}
                                disabled={!checked}
                                className="ml-auto h-8 w-[210px] text-xs"
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
